import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { buildResolver } from "../schema/buildResolver";
import { useToast } from "../../../_context/ToastContext/ToastContext";

// Ported 1:1 from CompanyFormModal's old `initialState` useMemo (see git
// history) - same per-type default-value rules, just factored out so any
// dynamic-form consumer gets them for free.
function buildDefaultValues(schema, mode, rowData) {
  if (!Array.isArray(schema)) return {};

  return schema.reduce((acc, section) => {
    (section.fields || []).forEach((field) => {
      if (!field.name) return;

      if (field.type === "checkbox" || field.type === "checkboxContainer") {
        acc[field.name] =
          mode === "edit"
            ? rowData?.[field.name] ?? field.defaultValue ?? false
            : field.defaultValue ?? field.value ?? false;
      } else if (field.type === "listManager" || field.type === "arrayText") {
        acc[field.name] =
          mode === "edit"
            ? rowData?.[field.name] ?? field.defaultValue ?? []
            : field.defaultValue ?? field.value ?? [];
      } else if (field.type === "walletCreditsDynamic") {
        acc[field.name] =
          mode === "edit"
            ? rowData?.walletCredits ?? field.defaultValue ?? []
            : field.defaultValue ?? [];
      } else {
        acc[field.name] =
          mode === "edit"
            ? rowData?.[field.name] ?? field.defaultValue ?? ""
            : field.defaultValue ?? field.value ?? "";
      }
    });
    return acc;
  }, {});
}

/**
 * react-hook-form wrapper for a dynamic (object-customization) form schema.
 * Builds defaultValues + a validation resolver straight from the field
 * descriptors - the same descriptor contract the old renderInput/
 * buildValidationSchema engine used, so schemas don't need rewriting to
 * move onto this.
 *
 * `schema` is expected to change after mount (useResolveFormSchema's
 * reference-data queries resolve asynchronously) - RHF only applies
 * `defaultValues` on the very first render, so later schema changes are
 * merged in manually below, only into fields the user hasn't already
 * started filling in. This replaces CompanyFormModal's old manual merge
 * effect in useFormValidation, and RHF's own re-render-on-change (via
 * `watch()`) replaces its `setSectionRenderTrigger` forced-render hack.
 */
export function useDynamicForm(schema, { mode = "create", rowData } = {}) {
  const initialDefaultValues = useMemo(
    () => buildDefaultValues(schema, mode, rowData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const resolver = useMemo(() => buildResolver(schema, mode), [schema, mode]);

  const form = useForm({
    defaultValues: initialDefaultValues,
    resolver,
    mode: "onBlur",
    shouldUnregister: false,
  });

  const { getValues, setValue, watch, formState, trigger } = form;
  const toast = useToast();

  // react-hook-form drops an invalid submit silently unless an invalid-handler is given, so a form
  // blocked by a field that has no visible error (hidden, off-screen or unrendered) looked like a
  // dead Submit button (QA-169). Every dynamic form now says why, and focuses the field if it can.
  const baseHandleSubmit = form.handleSubmit;
  const handleSubmit = (onValid, onInvalid) =>
    baseHandleSubmit(
      onValid,
      onInvalid ||
        ((errors) => {
          const [name, err] = Object.entries(errors || {})[0] || [];
          const message = err?.message || (name ? `${name} is not valid` : "Please check the form");
          toast?.showToast?.(message, "error");
          if (name) {
            try {
              form.setFocus(name);
            } catch (_) {}
          }
        })
    );

  const latestDefaultValues = useMemo(
    () => buildDefaultValues(schema, mode, rowData),
    [schema, mode, rowData]
  );

  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    const current = getValues();
    Object.keys(latestDefaultValues).forEach((key) => {
      const currentValue = current[key];
      const isUnset =
        !(key in current) ||
        currentValue === undefined ||
        currentValue === null ||
        currentValue === "";
      if (isUnset) {
        setValue(key, latestDefaultValues[key]);
      }
    });
    // Only re-run when the schema-derived defaults actually change - not on
    // every keystroke (`getValues`/`setValue` identities are stable from RHF).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestDefaultValues]);

  const formData = watch();

  const legacyErrors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(formState.errors).map(([key, err]) => [key, err?.message])
      ),
    [formState.errors]
  );

  const handleChange = (name, value, { forceRevalidate = false } = {}) => {
    setValue(name, value, { shouldDirty: true });
    // Once a field has already shown an error (e.g. "required", from a
    // prior blur/submit), revalidate it immediately on change instead of
    // waiting for the next blur - matches RHF's own reValidateMode:
    // "onChange" behavior for native Controller-driven fields, which this
    // bridged renderInput path doesn't get for free since it calls
    // setValue directly instead of going through Controller's onChange.
    //
    // `forceRevalidate` (opt-in per call site - FormField's singleSelect/
    // date cases) skips the "already has an error" check: a field whose
    // value is set via a portaled dropdown/picker can pick up a stale
    // error from an async validation pass that started on blur (reading
    // the pre-selection value) but lands *after* this onChange - at the
    // moment this runs, formState.errors[name] may still be empty, so the
    // conditional trigger above would (correctly, by its own logic, but
    // insufficiently here) skip revalidating. Forcing trigger() to run
    // regardless queues a second validation pass strictly after the stale
    // one, so its correct result is what actually lands in formState.
    if (forceRevalidate || formState.errors[name]) {
      trigger(name);
    }
  };
  const handleBlur = (name) => trigger(name);

  return {
    ...form,
    handleSubmit,
    formData,
    errors: legacyErrors,
    handleChange,
    handleBlur,
  };
}
